import { Injectable } from '@angular/core';
import { ModalController, ToastController, AlertController, LoadingController, ActionSheetController, ActionSheetOptions } from '@ionic/angular';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';

@Injectable({
    providedIn: 'root'
})
export class Metodosui {

    private loader: HTMLIonLoadingElement | null = null;

    constructor(
        private modalCtrl: ModalController,
        private toastCtrl: ToastController,
        private alertCtrl: AlertController,
        private loadingCtrl: LoadingController,
        private actionSheetCtrl: ActionSheetController
    ) { }

    async openModal(component: any, componentProps: any = {}, cssClass: string = ''): Promise<any> {
        const modal = await this.modalCtrl.create({
            component: component,
            componentProps: componentProps,
            cssClass: cssClass
        });

        await modal.present();

        const { data } = await modal.onDidDismiss();
        return data;
    }

    async closeModal(data?: any) {
        await this.modalCtrl.dismiss(data);
    }

    async takePicture(source: CameraSource = CameraSource.Prompt): Promise<any> {
        try {
            const image = await Camera.getPhoto({
                quality: 90,
                allowEditing: false,
                resultType: CameraResultType.Base64,
                source: source
            });
            return image;
        } catch (error) {
            console.error('Error taking picture:', error);
            return null;
        }
    }

    async showToast(message: string, color: string = 'dark', position: 'top' | 'bottom' | 'middle' = 'bottom', duration: number = 2500) {
        // Map color to appropriate icon
        let icon = '';
        switch (color) {
            case 'success': icon = 'checkmark-circle-outline'; break;
            case 'danger': icon = 'alert-circle-outline'; break;
            case 'warning': icon = 'warning-outline'; break;
            case 'primary': icon = 'information-circle-outline'; break;
            case 'tertiary': icon = 'notifications-outline'; break;
            default: icon = 'notifications-outline'; break;
        }

        const toast = await this.toastCtrl.create({
            message: message,
            duration: duration,
            icon: icon,
            buttons: [
                {
                    icon: 'close',
                    role: 'cancel',
                    side: 'end'
                }
            ],
            cssClass: `premium-toast toast-${color}`,
            position: position,
            mode: 'ios' // Consistent look
        });
        toast.present();
    }

    async showLoading(message: string = 'Cargando...', color: 'red' | 'blue' | 'gray' | 'green' | 'orange' = 'red') {
        // Just in case one is already showing
        if (this.loader) {
            this.loader.message = message;
            return;
        }

        this.loader = await this.loadingCtrl.create({
            message: message,
            spinner: 'circular',
            cssClass: `premium-loading loading-${color}`,
            backdropDismiss: false
        });
        await this.loader.present();
    }

    /**
     * Muestra un indicador de carga premium Soft-UI (loadingpgv2) acorde a la paleta Pinturas García / alertpgv2.
     */
    async showLoadingPgv2(message: string = 'Cargando...'): Promise<void> {
        if (this.loader) {
            this.loader.message = message;
            return;
        }

        this.loader = await this.loadingCtrl.create({
            message: message,
            spinner: 'crescent',
            mode: 'ios',
            cssClass: 'loadingpgv2',
            backdropDismiss: false
        });
        await this.loader.present();
    }

    async hideLoading() {
        if (this.loader) {
            try {
                // We add a tiny delay to prevent race condition between present() and dismiss()
                // which is a common issue in Ionic's LoadingController
                await this.loader.dismiss();
            } catch (e) {
                console.warn('Error hiding loader:', e);
            } finally {
                this.loader = null;
            }
        }
    }

    async hideLoadingPgv2() {
        await this.hideLoading();
    }

    /**
     * Muestra una alerta informativa con un único botón de cierre.
     * Úsala en lugar de instanciar AlertController directamente en los componentes.
     */
    async showAlert(header: string, message: string, buttonText: string = 'Entendido'): Promise<void> {
        const alert = await this.alertCtrl.create({
            header,
            message,
            cssClass: 'custom-alert',
            buttons: [{ text: buttonText, role: 'cancel' }]
        });
        await alert.present();
        await alert.onDidDismiss();
    }

    /**
     * Muestra una alerta con un campo de texto (textarea) y botones de Cancelar/Guardar.
     * @returns El texto ingresado, o null si se canceló.
     */
    async showInputAlert(header: string, subHeader: string, placeholder: string = ''): Promise<string | null> {
        return new Promise(async (resolve) => {
            const alert = await this.alertCtrl.create({
                header,
                subHeader,
                cssClass: 'custom-alert',
                inputs: [
                    {
                        name: 'value',
                        type: 'textarea',
                        placeholder,
                        cssClass: 'custom-textarea'
                    }
                ],
                buttons: [
                    {
                        text: 'Cancelar',
                        role: 'cancel',
                        cssClass: 'alert-button btn-cancel',
                        handler: () => resolve(null)
                    },
                    {
                        text: 'Guardar',
                        cssClass: 'alert-button btn-save',
                        handler: (data) => resolve(data.value ? data.value.trim() : '')
                    }
                ]
            });
            await alert.present();
        });
    }

    /**
     * Muestra una alerta de confirmación estándar o destructiva (eliminación/desvinculación).
     * @param header     Título del diálogo.
     * @param message    Cuerpo del mensaje.
     * @param confirmText Texto del botón de confirmación. Default: 'Aceptar'.
     * @param destructive Si es true aplica estilo rojo y role destructive. Default: false.
     * @param customClass Clase CSS adicional para personalizar el alert. Default: ''.
     */
    async showConfirmation(
        header: string,
        message: string,
        confirmText: string = 'Aceptar',
        destructive: boolean = false,
        customClass: string = ''
    ): Promise<boolean> {
        return new Promise(async (resolve) => {
            const alert = await this.alertCtrl.create({
                header: destructive ? header.toUpperCase() : header,
                message,
                cssClass: `${destructive ? 'custom-alert alert-danger' : 'custom-alert'} ${customClass}`.trim(),
                buttons: [
                    {
                        text: 'Cancelar',
                        role: 'cancel',
                        cssClass: 'alert-button btn-cancel',
                        handler: () => resolve(false)
                    },
                    {
                        text: confirmText,
                        role: destructive ? 'destructive' : 'primary',
                        cssClass: destructive ? 'alert-button danger-btn' : 'alert-button success-btn',
                        handler: () => resolve(true)
                    }
                ]
            });
            await alert.present();
        });
    }

    /**
     * Muestra una alerta informativa dark luxury (alertpgv2) inspirada en Carwash con paleta Pinturas García.
     */
    async showAlertPgV2(
        header: string,
        message: string,
        subHeader?: string,
        buttonText: string = 'Entendido',
        isPayment: boolean = false
    ): Promise<void> {
        const btnClass = isPayment ? 'alert-btn-emerald' : 'alert-btn-primary';
        const alert = await this.alertCtrl.create({
            header,
            subHeader,
            message,
            mode: 'ios',
            cssClass: 'alertpgv2',
            buttons: [
                {
                    text: buttonText,
                    role: 'cancel',
                    cssClass: btnClass
                }
            ]
        });
        await alert.present();
        await alert.onDidDismiss();
    }

    /**
     * Muestra un diálogo de confirmación dark luxury (alertpgv2) con botones verticales estilo Carwash.
     */
    async showConfirmationPgV2(options: {
        header: string;
        subHeader?: string;
        message: string;
        confirmText?: string;
        cancelText?: string;
        destructive?: boolean;
        isPayment?: boolean;
        customClass?: string;
    }): Promise<boolean> {
        return new Promise(async (resolve) => {
            const confirmClass = options.destructive ? 'danger-btn' : (options.isPayment ? 'alert-btn-emerald' : 'alert-btn-primary');
            const alert = await this.alertCtrl.create({
                header: options.destructive ? options.header.toUpperCase() : options.header,
                subHeader: options.subHeader,
                message: options.message,
                mode: 'ios',
                cssClass: `alertpgv2 ${options.customClass || ''}`.trim(),
                buttons: [
                    {
                        text: options.cancelText || 'Cancelar',
                        role: 'cancel',
                        cssClass: 'alert-btn-cancel',
                        handler: () => resolve(false)
                    },
                    {
                        text: options.confirmText || 'Confirmar',
                        role: options.destructive ? 'destructive' : 'primary',
                        cssClass: confirmClass,
                        handler: () => resolve(true)
                    }
                ]
            });
            await alert.present();
        });
    }

    /**
     * Muestra un diálogo con campo de entrada (Prompt) dark luxury (alertpgv2) con botones verticales estilo Carwash.
     */
    async showInputAlertPgV2(options: {
        header: string;
        subHeader?: string;
        message?: string;
        placeholder?: string;
        type?: any;
        initialValue?: string;
        confirmText?: string;
        cancelText?: string;
        isPayment?: boolean;
    }): Promise<string | null> {
        return new Promise(async (resolve) => {
            const confirmClass = options.isPayment ? 'alert-btn-emerald' : 'alert-btn-primary';
            const alert = await this.alertCtrl.create({
                header: options.header,
                subHeader: options.subHeader,
                message: options.message,
                mode: 'ios',
                cssClass: 'alertpgv2',
                inputs: [
                    {
                        name: 'value',
                        type: options.type || 'text',
                        placeholder: options.placeholder || '',
                        value: options.initialValue || '',
                        attributes: {
                            step: 'any',
                            min: '0'
                        }
                    }
                ],
                buttons: [
                    {
                        text: options.cancelText || 'Cancelar',
                        role: 'cancel',
                        cssClass: 'alert-btn-cancel',
                        handler: () => resolve(null)
                    },
                    {
                        text: options.confirmText || 'Aceptar',
                        cssClass: confirmClass,
                        handler: (data) => resolve(data.value !== undefined ? String(data.value).trim() : '')
                    }
                ]
            });
            await alert.present();
        });
    }

    /**
     * Muestra un Action Sheet con diseño oficial P. García / dark luxury (custom-action-sheet).
     */
    async showActionSheetPgV2(options: ActionSheetOptions): Promise<HTMLIonActionSheetElement> {
        const actionSheet = await this.actionSheetCtrl.create({
            ...options,
            cssClass: `custom-action-sheet ${options.cssClass || ''}`.trim()
        });
        await actionSheet.present();
        return actionSheet;
    }
}
